import { LmsIcsFetchError } from './lmsIcsFetchError';

/**
 * Fetch ICS document text from a calendar feed URL (e.g. Canvas personal feed).
 * Uses omit credentials to avoid sending site cookies cross-origin.
 *
 * @param {string} feedUrl
 * @param {AbortSignal} [signal]
 * @returns {Promise<string>}
 */
export async function fetchIcsText(feedUrl, signal) {
    const url = (feedUrl || '').trim();
    if (!url) {
        throw new LmsIcsFetchError('empty_feed_url', 'ICS feed URL is empty', 0);
    }

    let parsed;
    try {
        parsed = new URL(url);
    } catch {
        throw new LmsIcsFetchError('invalid_feed_url', 'ICS feed URL is invalid', 0);
    }

    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        throw new LmsIcsFetchError('invalid_feed_url', 'ICS feed URL must be http(s)', 0);
    }

    let res;
    try {
        res = await fetch(url, {
            method: 'GET',
            credentials: 'omit',
            redirect: 'follow',
            signal,
            headers: {
                Accept: 'text/calendar, application/ics, text/plain;q=0.9, */*;q=0.1',
            },
        });
    } catch (e) {
        const msg = e instanceof Error ? e.message : 'Network error';
        throw new LmsIcsFetchError('feed_fetch_failed', msg, 0);
    }

    if (!res.ok) {
        if (res.status === 403 || res.status === 401) {
            throw new LmsIcsFetchError(
                'feed_forbidden',
                'The calendar feed denied access. Re-link your LMS account to refresh the feed URL.',
                res.status,
            );
        }
        throw new LmsIcsFetchError(
            'feed_fetch_failed',
            `ICS fetch failed: ${res.status} ${res.statusText}`,
            res.status,
        );
    }

    const text = await res.text();
    if (!text || !text.trim()) {
        throw new LmsIcsFetchError('feed_empty', 'Calendar feed returned no data.', res.status);
    }

    return text;
}
